import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  EcommerceHubHeader,
  ProductDescriptionTab,
  ProductTitleTab,
  CategoryPageTab,
  ProductReviewTab,
  BulkImportTab
} from './ecommerce-hub';

export type EcommerceHubTabType = 'product-descriptions' | 'product-titles' | 'category-pages' | 'product-reviews' | 'bulk-import';

const EcommerceHub: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeTab, setActiveTab] = useState<EcommerceHubTabType>('product-descriptions');

  // Parse hash from URL to set active tab
  useEffect(() => {
    const hash = location.hash.slice(1) as EcommerceHubTabType;
    if (['product-descriptions', 'product-titles', 'category-pages', 'product-reviews', 'bulk-import'].includes(hash)) {
      setActiveTab(hash);
    } else {
      setActiveTab('product-descriptions');
    }
  }, [location.hash]);

  // Update URL hash when tab changes
  const handleTabChange = (tab: EcommerceHubTabType) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <EcommerceHubHeader />

        {/* Tab Navigation */}
        <div className="mt-6 rounded-2xl bg-white shadow-sm ring-1 ring-gray-100">
          <div className="border-b border-gray-200 px-6">
            <nav className="flex gap-6 overflow-x-auto" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('product-descriptions')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'product-descriptions'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'product-descriptions' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span>Descriptions</span>
              </button>
              
              <button
                onClick={() => handleTabChange('product-titles')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'product-titles'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'product-titles' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>Titles</span>
              </button>
              
              <button
                onClick={() => handleTabChange('category-pages')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'category-pages'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'category-pages' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span>Category Pages</span>
              </button>
              
              <button
                onClick={() => handleTabChange('product-reviews')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'product-reviews'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'product-reviews' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
                <span>Reviews</span>
              </button>
              
              <button
                onClick={() => handleTabChange('bulk-import')}
                className={`
                  group inline-flex items-center gap-2 border-b-2 px-1 py-4 text-sm font-medium transition whitespace-nowrap
                  ${activeTab === 'bulk-import'
                    ? 'border-orange-600 text-orange-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }
                `}
                aria-current={activeTab === 'bulk-import' ? 'page' : undefined}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span>Bulk Import</span>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'product-descriptions' && <ProductDescriptionTab />}
            {activeTab === 'product-titles' && <ProductTitleTab />}
            {activeTab === 'category-pages' && <CategoryPageTab />}
            {activeTab === 'product-reviews' && <ProductReviewTab />}
            {activeTab === 'bulk-import' && <BulkImportTab />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EcommerceHub;
